import { useState, useEffect, useCallback } from 'react';
import { register, login, logout, isAuthenticated } from '../api/client';
import type { Trainer, RegisterInput, LoginInput } from '../types';

interface AuthState {
  trainer: Trainer | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface UseAuthReturn extends AuthState {
  register: (input: RegisterInput) => Promise<void>;
  login: (input: LoginInput) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

const STORAGE_KEY = 'ptai_trainer';

function getStoredTrainer(): Trainer | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    localStorage.removeItem(STORAGE_KEY);
  }
  return null;
}

function setStoredTrainer(trainer: Trainer): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trainer));
}

function removeStoredTrainer(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    trainer: getStoredTrainer(),
    isAuthenticated: isAuthenticated(),
    isLoading: false,
    error: null,
  });

  useEffect(() => {
    // Sync state with token status on mount
    const auth = isAuthenticated();
    if (!auth && state.isAuthenticated) {
      setState(prev => ({
        ...prev,
        trainer: null,
        isAuthenticated: false,
      }));
      removeStoredTrainer();
    }
  }, []);

  const handleRegister = useCallback(async (input: RegisterInput): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await register(input);
      setStoredTrainer(response.trainer);
      setState({
        trainer: response.trainer,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      throw err;
    }
  }, []);

  const handleLogin = useCallback(async (input: LoginInput): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await login(input);
      setStoredTrainer(response.trainer);
      setState({
        trainer: response.trainer,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      throw err;
    }
  }, []);

  const handleLogout = useCallback((): void => {
    logout();
    removeStoredTrainer();
    setState({
      trainer: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  const clearError = useCallback((): void => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    register: handleRegister,
    login: handleLogin,
    logout: handleLogout,
    clearError,
  };
}
